import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaGithub, FaLinkedin, FaArrowRight, FaDownload, FaArrowUp } from 'react-icons/fa';
import { API_BASE, CV_URL } from '../config';
import { useLanguage } from '../context/LanguageContext';
import Newsletter from '../components/newsletter';

const Home = () => {
  const [about, setAbout] = useState(null);
  const [projects, setProjects] = useState([]);
  const [skills, setSkills] = useState([]);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const { t } = useLanguage();

  const handleDownloadCV = async () => {
    const cvEndpoint = API_BASE ? `${API_BASE}/api/cv` : CV_URL;
    try {
      const response = await fetch(cvEndpoint);
      if (!response.ok) throw new Error('Download failed');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'Bernard_Banda_Cv.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('CV download error:', err);
      window.open(cvEndpoint, '_blank', 'noopener,noreferrer');
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/portfolio`);
        const data = await res.json();
        setAbout(data.about);
        setProjects(data.projects ? data.projects.slice(0, 3) : []);
        setSkills(data.skills ? data.skills.slice(0, 6) : []);
      } catch (error) {
        console.error('Error fetching data:', error);
        setAbout({
          name: 'Computer Science Student',
          title: 'AI Software Developer',
          bio: 'Passionate about building AI-powered web-based and software systems.',
          profile_image: '/images/profile/Myimage.jpeg'
        });
        setProjects([
          {
            id: 6,
            title: 'Smart Agrotrack',
            description: 'Automated Greenhouse Monitoring & Control System.',
            image_url: '/images/projects/Automated_Greebhouse.png',
            technologies: ['C#', 'Arduino IDE', 'Soil moisture sensor', 'Light & temperature sensor'],
            live_link: 'https://www.linkedin.com/feed/update/urn:li:activity:7448353870843473920/',
          },
          {
            id: 5,
            title: 'Inventory Management and stock taking app',
            description: 'A platform developed for africa university Fleet and Facilities Unit (FFU)',
            image_url: '/images/projects/FFUI.png',
            technologies: ['React', 'Node.js', 'Express', 'PostgreSQL'],
          },
          {
            id: 2,
            title: 'AU Transcript Clone',
            description: 'A clone of Africa University transcript.',
            image_url: '/images/projects/TableOrange.png',
            technologies: ['HTML', 'CSS'],
            github_link: 'https://github.com/BenBand/School-Results-Table',
            live_link: 'https://schooltimetemplate.netlify.app/'
          },
        ]);
        setSkills([
          { id: 1, name: 'JavaScript', category: 'languages' },
          { id: 2, name: 'React', category: 'frontend' },
          { id: 3, name: 'Node.js', category: 'backend' },
          { id: 4, name: 'PostgreSQL', category: 'backend' },
          { id: 5, name: 'Python', category: 'languages' },
          { id: 6, name: 'HTML/CSS', category: 'frontend' },
        ]);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 300);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="home">
      <section className="hero">
        <div className="hero-content">
          <div className="hero-text fade-in-up">
            <p className="hero-greeting">{t('home.greeting')}</p>
            <h1 className="hero-title">
              {about?.name || t('home.title')}
            </h1>
            <p className="hero-subtitle">
              {t('home.subtitle')}
            </p>
            <div className="hero-buttons" data-tour="hero-actions">
              <Link to="/projects" className="btn-primary">
                {t('home.viewProjects')} <FaArrowRight />
              </Link>
              <button type="button" onClick={handleDownloadCV} className="btn-secondary">
                <FaDownload /> {t('home.downloadCV')}
              </button>
              <Link to="/contact" className="btn-secondary">
                {t('home.getInTouch')}
              </Link>
            </div>
            <div className="social-links" style={{ marginTop: '2rem' }}>
              <a href="https://github.com/BenBand" target="_blank" rel="noopener noreferrer" className="social-link">
                <FaGithub />
              </a>
              <a href="https://www.linkedin.com/in/bernard-banda-6995b633b/" target="_blank" rel="noopener noreferrer" className="social-link">
                <FaLinkedin />
              </a>
            </div>
          </div>
          <div className="hero-image fade-in-up stagger-2">
            <div className="hero-image-container">
              <div className="hero-image-inner">
                <img src={about?.profile_image || '/images/profile/Myimage.jpeg'} alt="Profile" />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section" style={{ background: '#fff' }}>
        <h2 className="section-title">{t('home.skillsTech')}</h2>
        <div className="skills-grid" style={{ maxWidth: '800px', margin: '0 auto' }}>
          {skills.map((skill) => (
            <div key={skill.id} className="skill-item">
              <span>{skill.name}</span>
            </div>
          ))}
        </div>
        <div style={{ textAlign: 'center', marginTop: '2rem' }}>
          <Link to="/about" className="btn-primary">
            {t('home.viewAllSkills')} <FaArrowRight />
          </Link>
        </div>
      </section>

      <section className="section projects">
        <h2 className="section-title">{t('home.featuredProjects')}</h2>
        <div className="projects-grid">
          {projects.map((project, index) => (
            <div key={project.id} className={`project-card fade-in-up stagger-${index + 1}`}>
              <div className="project-image">
                <img src={project.image_url || '/ProjectsImages/FFUI.png'} alt={project.title} />
                <div className="project-overlay"></div>
              </div>
              <div className="project-content">
                <h3>{project.title}</h3>
                <p>{project.description}</p>
                <div className="tech-tags">
                  {project.technologies?.map((tech, i) => (
                    <span key={i} className="tech-tag">{tech}</span>
                  ))}
                </div>
                <div className="project-links">
                  {project.github_link && (
                    <a href={project.github_link} target="_blank" rel="noopener noreferrer" className="project-link">
                      <FaGithub /> {t('home.code')}
                    </a>
                  )}
                  {project.live_link && (
                    <a href={project.live_link} target="_blank" rel="noopener noreferrer" className="project-link">
                      <FaArrowRight /> {t('home.liveDemo')}
                    </a>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
        <div style={{ textAlign: 'center', marginTop: '3rem' }}>
          <Link to="/projects" className="btn-primary">
            {t('home.viewProjects')} <FaArrowRight />
          </Link>
        </div>
      </section>

      <Newsletter />

      {showBackToTop && (
        <button onClick={scrollToTop} className="back-to-top show" aria-label={t('home.backToTop')} style={{ position: 'fixed', bottom: '24px', left: '24px', width: '50px', height: '50px', borderRadius: '50%', background: 'var(--gradient-primary)', border: 'none', color: 'white', fontSize: '1.25rem', cursor: 'pointer', boxShadow: '0 8px 24px rgba(0, 173, 181, 0.4), var(--shadow-lg)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9998 }}>
          <FaArrowUp />
        </button>
      )}
    </div>
  );
};

export default Home;
